import React, { useState } from 'react';

const ForgotEmailModal = ({ onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emails, setEmails] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Name validation
    if (!firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      setLoading(false);
      return;
    }

    if (firstName.length > 50) {
      setError('First name is too long (max 50 characters)');
      setLoading(false);
      return;
    }

    if (lastName.length > 50) {
      setError('Last name is too long (max 50 characters)');
      setLoading(false);
      return;
    }

    // Name format validation (letters, spaces, hyphens only)
    const nameRegex = /^[a-zA-Z\s-]+$/;
    if (!nameRegex.test(firstName)) {
      setError('First name can only contain letters, spaces, and hyphens');
      setLoading(false);
      return;
    }

    if (!nameRegex.test(lastName)) {
      setError('Last name can only contain letters, spaces, and hyphens');
      setLoading(false);
      return;
    }

    try {
      if (!firstName.trim() || !lastName.trim()) {
        setError('Please enter both first name and last name');
        setLoading(false);
        return;
      }

      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      
      const response = await fetch(`${apiBaseUrl}/CommonChannel/GetForgetEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          User_First_Name: firstName,
          User_Last_Name: lastName,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve email addresses');
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        setError('No accounts found with the provided name. Please check your spelling and try again.');
        setLoading(false);
        return;
      }

      setEmails(data);
      setShowResults(true);

    } catch (error) {
      console.error('Forgot email error:', error);
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slideInUp">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Forgot Email?</h2>
          <p className="text-gray-600 text-sm">Enter your name to retrieve your registered email address.</p>
        </div>

        {showResults ? (
          <div className="space-y-4">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-green-800 font-semibold mb-2">Email Address(es) Found:</p>
                  <div className="space-y-2">
                    {emails.map((item, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-gray-900 font-medium">{item.user_Email || item.userEmail}</p>
                        <p className="text-gray-600 text-sm">{item.user_First_Name} {item.user_Last_Name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full bg-purple-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-purple-700 transition-all duration-200 shadow-lg"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all duration-200 text-base text-gray-900"
                placeholder="First Name"
                disabled={loading}
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all duration-200 text-base text-gray-900"
                placeholder="Last Name"
                disabled={loading}
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-purple-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-purple-700 transition-all duration-200 disabled:opacity-50 shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Searching...
                  </span>
                ) : (
                  'Get Info'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotEmailModal;
